import React from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { sensorFullNameKey, sensorUnits } from 'commons/src/constants';
import { Rating } from 'commons/src/models/commonEnums';
import { ThresholdRange } from 'commons/src/models/commonTypeScript';
import {
    sensorRating,
    formatThresholdNumber,
    getStatusColor,
    sensorShowsRatingLabels,
    SpaceMeasurement,
} from 'commons/src/sensorRatingUtils';
import { IaqInsightData, PDFContext, ReportSensorThresholds } from '../../../../../models/common';
import {
    formatSensorValue,
    formatSensorUnit,
    getSensorIconSource,
    getStatusPillColors,
    SpaceMeasurementDataColumns,
} from '../utils/pdfHelpers';
import iaqInsight2PdfStyles from '../utils/pdfStyles';
import IaqInsight2DataTable from './IaqInsight2DataTable';

const getRangeDisplayUnit = (unit: keyof typeof sensorUnits): string => {
    if (unit === 'c') return `${sensorUnits[unit]}C`;
    if (unit === 'f') return `${sensorUnits[unit]}F`;
    return sensorUnits[unit];
};

const translatedFormatRangeText = (range: ThresholdRange, unit: keyof typeof sensorUnits, t: PDFContext): string => {
    const displayUnit = getRangeDisplayUnit(unit);
    if (range.from !== undefined && range.to !== undefined) {
        return `${formatThresholdNumber(range.from)} - ${formatThresholdNumber(range.to)} ${displayUnit}`;
    }
    if (range.from !== undefined && range.to === undefined) {
        return `${t('IAQReport2.Above')} ${formatThresholdNumber(range.from)} ${displayUnit}`;
    }
    if (range.to !== undefined && range.from === undefined) {
        return `${t('IAQReport2.Below')} ${formatThresholdNumber(range.to)} ${displayUnit}`;
    }
    return '';
};

type IaqInsight2SensorSectionProps = {
    translationFunction: PDFContext;
    sensorType: string;
    sensorThresholdConfiguration: ReportSensorThresholds;
    sensorReportData: IaqInsightData;
    spaceMeasurementColumns: SpaceMeasurementDataColumns;
    listOfImages: { data: string; serialNumber: string }[];
    hideCharts?: boolean;
};

const IaqInsight2SensorSection = ({
    translationFunction,
    sensorType,
    sensorThresholdConfiguration,
    sensorReportData,
    spaceMeasurementColumns,
    listOfImages,
    hideCharts,
}: IaqInsight2SensorSectionProps): React.ReactElement => {
    const image = listOfImages.length > 0 && listOfImages.find(chart => chart.serialNumber === sensorType);
    const { ranges: thresholdRanges, unit: measurementUnit } = sensorThresholdConfiguration;
    const formattedSensorDisplayName = translationFunction(sensorFullNameKey(sensorType)).replace('CO₂', 'CO2');
    const formattedSensorDescription = translationFunction(`IAQReport2.${sensorType}Description`).replace(
        /CO₂/gi,
        'CO2'
    );
    const formattedMeasurementUnit = formatSensorUnit(sensorType, measurementUnit);

    const { averageValueWithinOpeningHours, averageValue: overallAverageValue, averageGraph } = sensorReportData;

    const hasSpacesWithData = Object.values(
        sensorReportData.spaceOpeningHoursValues || sensorReportData.devicesOpeningHoursValues || {}
    ).some(
        spaceData =>
            spaceData.average !== undefined &&
            spaceData.average !== null &&
            !Number.isNaN(spaceData.average) &&
            spaceData.average !== 0
    );

    const hasGraphData = averageGraph && averageGraph.length > 0;

    const hasValidData = hasSpacesWithData || hasGraphData;

    const averageWithinOpeningHoursRating = hasValidData
        ? sensorRating(thresholdRanges, averageValueWithinOpeningHours)
        : undefined;
    const overallAverageValueRating = hasValidData ? sensorRating(thresholdRanges, overallAverageValue) : undefined;

    const spaceMeasurementValuesArray: SpaceMeasurement[] = Object.values(
        sensorReportData.spaceOpeningHoursValues || sensorReportData.devicesOpeningHoursValues || {}
    );

    // Calculate spaces with poor overall average (based on average value)
    const spacesWithPoorAverage = spaceMeasurementValuesArray.filter(space => {
        if (space.average === undefined || space.average === null || Number.isNaN(space.average)) {
            return false;
        }
        const rating = sensorRating(thresholdRanges, space.average);
        return rating === Rating.POOR;
    }).length;

    // Calculate spaces with poor highest/lowest levels (either highest OR lowest is poor)
    const spacesWithPoorExtremes = spaceMeasurementValuesArray.filter(space => {
        let hasPoorExtreme = false;

        if (
            space.highestMeasurement !== undefined &&
            space.highestMeasurement !== null &&
            !Number.isNaN(space.highestMeasurement)
        ) {
            const highestRating = sensorRating(thresholdRanges, space.highestMeasurement);
            if (highestRating === Rating.POOR) {
                hasPoorExtreme = true;
            }
        }

        if (
            space.lowestMeasurement !== undefined &&
            space.lowestMeasurement !== null &&
            !Number.isNaN(space.lowestMeasurement)
        ) {
            const lowestRating = sensorRating(thresholdRanges, space.lowestMeasurement);
            if (lowestRating === Rating.POOR) {
                hasPoorExtreme = true;
            }
        }

        return hasPoorExtreme;
    }).length;

    const totalSpaces = spaceMeasurementValuesArray.length;

    const countMarker = '\u200B';
    const spacesOutOfSuffix =
        translationFunction('IAQReport2.SpacesOutOf', {
            count: countMarker,
            total: String(totalSpaces),
        }).split(countMarker)[1] || '';

    const sensorIconImageSource = getSensorIconSource(sensorType);
    const averageWithinOpeningHoursStatusColor = getStatusColor(averageWithinOpeningHoursRating || Rating.GOOD);
    const overallAverageStatusColor = getStatusColor(overallAverageValueRating || Rating.GOOD);

    return (
        <View>
            {/* Sensor Header: Icon + Name on left, Threshold pills on right */}
            <View style={iaqInsight2PdfStyles.sensorHeader} wrap={false}>
                <View style={iaqInsight2PdfStyles.sensorTitle}>
                    {sensorIconImageSource && (
                        <Image src={sensorIconImageSource} style={iaqInsight2PdfStyles.metricCardIcon} />
                    )}
                    <Text style={iaqInsight2PdfStyles.sensorName}>{formattedSensorDisplayName}</Text>
                </View>
                {sensorShowsRatingLabels(sensorType) && (
                    <View style={iaqInsight2PdfStyles.ratingLegend}>
                        {thresholdRanges
                            .filter(
                                thresholdRange =>
                                    translatedFormatRangeText(thresholdRange, measurementUnit, translationFunction) !==
                                    ''
                            )
                            .map(thresholdRange => {
                                const formattedThresholdRangeText = translatedFormatRangeText(
                                    thresholdRange,
                                    measurementUnit,
                                    translationFunction
                                );
                                const statusPillColors = getStatusPillColors(thresholdRange.rating);

                                const thresholdKey = `threshold-${thresholdRange.from ?? 'min'}-${
                                    thresholdRange.to ?? 'max'
                                }-${thresholdRange.rating}`;

                                return (
                                    <View key={thresholdKey} style={iaqInsight2PdfStyles.legendItem}>
                                        <View
                                            style={[
                                                iaqInsight2PdfStyles.thresholdPill,
                                                { backgroundColor: statusPillColors.backgroundColor },
                                            ]}
                                        >
                                            <Text
                                                style={[
                                                    iaqInsight2PdfStyles.pillText,
                                                    { color: statusPillColors.textColor },
                                                ]}
                                            >
                                                {translationFunction(`Ratings.${thresholdRange.rating}`)}
                                            </Text>
                                        </View>
                                        <Text style={iaqInsight2PdfStyles.legendRange}>
                                            {formattedThresholdRangeText}
                                        </Text>
                                    </View>
                                );
                            })}
                    </View>
                )}
            </View>

            {/* Description */}
            <Text style={iaqInsight2PdfStyles.description}>{formattedSensorDescription}</Text>

            {/* Summary Cards: 2x2 Grid Layout */}
            <View style={iaqInsight2PdfStyles.summaryCardsGrid} wrap={false}>
                {/* Top Row: Average Cards */}
                {/* Average within opening hours */}
                <View style={iaqInsight2PdfStyles.summaryCard} wrap={false}>
                    <Text style={iaqInsight2PdfStyles.summaryCardTitle}>
                        {translationFunction('IAQReport2.AverageWithinOpeningHours')}
                    </Text>
                    <View style={iaqInsight2PdfStyles.summaryCardValue}>
                        <Text style={iaqInsight2PdfStyles.summaryValue}>
                            {hasValidData &&
                            averageValueWithinOpeningHours !== undefined &&
                            averageValueWithinOpeningHours !== null &&
                            !Number.isNaN(averageValueWithinOpeningHours)
                                ? formatSensorValue(averageValueWithinOpeningHours)
                                : 'N/A'}
                        </Text>
                        {hasValidData &&
                            averageValueWithinOpeningHours !== undefined &&
                            averageValueWithinOpeningHours !== null &&
                            !Number.isNaN(averageValueWithinOpeningHours) && (
                                <Text style={iaqInsight2PdfStyles.summaryUnit}>{formattedMeasurementUnit}</Text>
                            )}
                    </View>
                    {sensorShowsRatingLabels(sensorType) && hasValidData && averageWithinOpeningHoursRating && (
                        <Text
                            style={[
                                iaqInsight2PdfStyles.summaryRating,
                                { color: averageWithinOpeningHoursStatusColor },
                            ]}
                        >
                            {translationFunction(`Ratings.${averageWithinOpeningHoursRating}`)}
                        </Text>
                    )}
                </View>

                {/* Average value */}
                <View style={iaqInsight2PdfStyles.summaryCard} wrap={false}>
                    <Text style={iaqInsight2PdfStyles.summaryCardTitle}>
                        {translationFunction('IAQReport2.AverageValueNoException')}
                    </Text>
                    <View style={iaqInsight2PdfStyles.summaryCardValue}>
                        <Text style={iaqInsight2PdfStyles.summaryValue}>
                            {hasValidData &&
                            overallAverageValue !== undefined &&
                            overallAverageValue !== null &&
                            !Number.isNaN(overallAverageValue)
                                ? formatSensorValue(overallAverageValue)
                                : 'N/A'}
                        </Text>
                        {hasValidData &&
                            overallAverageValue !== undefined &&
                            overallAverageValue !== null &&
                            !Number.isNaN(overallAverageValue) && (
                                <Text style={iaqInsight2PdfStyles.summaryUnit}>{formattedMeasurementUnit}</Text>
                            )}
                    </View>
                    {sensorShowsRatingLabels(sensorType) && hasValidData && overallAverageValueRating && (
                        <Text style={[iaqInsight2PdfStyles.summaryRating, { color: overallAverageStatusColor }]}>
                            {translationFunction(`Ratings.${overallAverageValueRating}`)}
                        </Text>
                    )}
                </View>

                {/* Detail Cards: Spaces with poor overall average */}
                {sensorShowsRatingLabels(sensorType) && (
                    <View style={iaqInsight2PdfStyles.detailCard} wrap={false}>
                        <Text style={iaqInsight2PdfStyles.detailLabel}>
                            {translationFunction('IAQReport2.SpacesWithOverallPoorLevels')}
                        </Text>
                        <View style={iaqInsight2PdfStyles.detailCardHeader}>
                            <Text style={iaqInsight2PdfStyles.detailCount}>
                                {hasValidData ? (
                                    <>
                                        <Text style={iaqInsight2PdfStyles.detailCountNumber}>
                                            {spacesWithPoorAverage}
                                        </Text>
                                        {spacesOutOfSuffix}
                                    </>
                                ) : (
                                    <Text style={iaqInsight2PdfStyles.summaryValue}>
                                        {translationFunction('IAQReport2.NotAvailable')}
                                    </Text>
                                )}
                            </Text>
                        </View>
                    </View>
                )}

                {/* Detail Cards: Spaces with poor highest/lowest levels */}
                {sensorShowsRatingLabels(sensorType) && (
                    <View style={iaqInsight2PdfStyles.detailCard} wrap={false}>
                        <Text style={iaqInsight2PdfStyles.detailLabel}>
                            {translationFunction('IAQReport2.SpacesWithPoorHighestLowestLevels')}
                        </Text>
                        <View style={iaqInsight2PdfStyles.detailCardHeader}>
                            <Text style={iaqInsight2PdfStyles.detailCount}>
                                {hasValidData ? (
                                    <>
                                        <Text style={iaqInsight2PdfStyles.detailCountNumber}>
                                            {spacesWithPoorExtremes}
                                        </Text>
                                        {spacesOutOfSuffix}
                                    </>
                                ) : (
                                    <Text style={iaqInsight2PdfStyles.summaryValue}>
                                        {translationFunction('IAQReport2.NotAvailable')}
                                    </Text>
                                )}
                            </Text>
                        </View>
                    </View>
                )}
            </View>

            {/* Chart Image: Display if available and not hidden */}
            {!hideCharts && image && image.data && (
                <>
                    <Text style={iaqInsight2PdfStyles.graphDescription}>
                        {translationFunction('IAQReport2.GraphDescription')}
                    </Text>
                    <View style={iaqInsight2PdfStyles.chartContainer} wrap={false}>
                        <Image src={image.data} style={iaqInsight2PdfStyles.chartImage} />
                    </View>
                </>
            )}

            {/* Data Table */}
            {hasSpacesWithData && spaceMeasurementColumns.length > 0 ? (
                <IaqInsight2DataTable
                    sensorType={sensorType}
                    translationFunction={translationFunction}
                    sensorThresholdConfiguration={sensorThresholdConfiguration}
                    spaceMeasurementColumns={spaceMeasurementColumns}
                />
            ) : (
                <View style={iaqInsight2PdfStyles.noDataContainer}>
                    <Text style={iaqInsight2PdfStyles.noDataText}>{translationFunction('IAQReport2.NoData')}</Text>
                </View>
            )}
        </View>
    );
};

export default IaqInsight2SensorSection;
